import type { SeasonStatus } from "@/lib/conditions/season-status";
import type { PerSportSeasonStatus } from "@/lib/conditions/unified-types";

type Props = {
  perSportStatus: PerSportSeasonStatus;
  activeSport: string;
  resortName: string;
};

export function SeasonBanner({ perSportStatus, activeSport, resortName }: Props) {
  const isSkiSport = activeSport === "piste" || activeSport === "off-piste";
  const isTouringSport = activeSport === "touring";
  const isMtbSport = activeSport === "mtb";

  let status: SeasonStatus | null = null;
  if (isSkiSport) status = perSportStatus.ski;
  else if (isTouringSport) status = perSportStatus.touring;
  else if (isMtbSport) status = perSportStatus.mtb;

  if (!status) return null;
  if (status.state === "in-season" && status.snowModifier !== "low-snow") return null;

  const sportLabel = isMtbSport ? "mountain biking" : isTouringSport ? "touring" : "skiing";

  if (status.state === "late-season") {
    return (
      <div className="mb-4">
        <div className="rounded-[12px] border border-amber-200/60 bg-amber-50/60 px-4 py-3 text-[13.5px] leading-relaxed text-amber-900/80">
          {status.snowModifier === "low-snow" && isSkiSport ? (
            <>
              Snow cover is very low for this time of season at {resortName}.
              Check the resort&apos;s website for current lift operations.
            </>
          ) : status.shoulder === "early" ? (
            <>
              Early season for {sportLabel}: {resortName} typically
              opens around {status.typicalOpenLabel}.
              {isSkiSport && " Some lifts may not yet be running. Check the resort's website for current operations."}
            </>
          ) : (
            <>
              Late season for {sportLabel}: {resortName} typically
              closes around {status.typicalCloseLabel}.
              {isSkiSport && " Some lifts may be closed. Check the resort's website for current operations."}
            </>
          )}
        </div>
      </div>
    );
  }

  if (status.state === "likely-closed") {
    return (
      <div className="mb-4">
        <div className="rounded-[12px] border border-stone-200/60 bg-stone-100/60 px-4 py-3 text-[13.5px] leading-relaxed text-stone-600">
          {isMtbSport ? "Mountain biking" : isTouringSport ? "Touring" : "Skiing"} at {resortName} is
          typically not available at this time of year. The season usually
          runs {status.typicalOpenLabel} to {status.typicalCloseLabel}.
          {status.snowModifier === "strong-snow" && (
            <>
              {" "}Snow conditions remain good. Check locally for extended operations.
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
}
